// ============================================================================
// KLINISOL CRO - Enhanced SOP Viewer with Link Injection
// Makes SOP numbers in Master Register clickable
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { 
    X, 
    Download, 
    FileText, 
    AlertCircle, 
    RotateCcw,
    Loader2,
    Info,
    ArrowLeft
} from 'lucide-react';

const SOPViewer = ({ sopId, sopNumber, fileType, onClose, fromMasterRegister }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [viewUrl, setViewUrl] = useState('');
    const [allSops, setAllSops] = useState([]);
    const [masterRegisterId, setMasterRegisterId] = useState(null);
    const iframeRef = useRef(null);

    useEffect(() => {
        loadAllSops();
        loadSopView();
    }, [sopId]);

    const loadAllSops = async () => {
        try {
            const response = await api.get('/sops');
            if (response.data.success) {
                setAllSops(response.data.data);
                
                // Find Master Register (OPS-Index-001)
                const masterRegister = response.data.data.find(sop => 
                    sop.sop_number === 'SOP-OPS-Index-001' || 
                    sop.sop_number === 'OPS-Index-001' ||
                    sop.title?.toLowerCase().includes('master sop register')
                );
                
                if (masterRegister) {
                    setMasterRegisterId(masterRegister.id);
                }
            }
        } catch (err) {
            console.error('Error loading SOPs list:', err);
        }
    };

    const loadSopView = async () => {
        try {
            setLoading(true);
            setError('');

            const response = await api.get(`/sops/${sopId}/view`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], {
                type: fileType === 'pdf' ? 'application/pdf' : 'text/html'
            });

            const url = URL.createObjectURL(blob);
            setViewUrl(url);

        } catch (err) {
            console.error('Error loading SOP view:', err);
            setError(err.response?.data?.message || 'Failed to load SOP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Inject clickable links after iframe loads (for Master Register)
    useEffect(() => {
        if (!viewUrl || !iframeRef.current || fileType === 'pdf') return;

        const iframe = iframeRef.current;
        
        const injectLinks = () => {
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                
                if (!iframeDoc) return;

                // Find all table cells that might contain SOP numbers
                const cells = iframeDoc.querySelectorAll('td strong');
                
                cells.forEach(cell => {
                    const text = cell.textContent.trim();
                    
                    // Match SOP number patterns (OPS-001, QMS-002, etc.)
                    const sopPattern = /^(OPS|QMS|DMS|REG|PVG)-\d{3}$/;
                    
                    if (sopPattern.test(text)) {
                        // Find matching SOP in database
                        const matchingSop = allSops.find(sop => 
                            sop.sop_number === text || 
                            sop.sop_number === `SOP-${text}`
                        );
                        
                        if (matchingSop) {
                            // Make it clickable
                            cell.style.cursor = 'pointer';
                            cell.style.color = '#0056B3';
                            cell.style.textDecoration = 'underline';
                            cell.title = `Click to view ${matchingSop.title}`;
                            
                            cell.onclick = (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                
                                // Close current viewer and open new SOP
                                onClose();
                                
                                // Trigger opening the new SOP with flag indicating it came from Master Register
                                setTimeout(() => {
                                    window.dispatchEvent(new CustomEvent('openSOP', {
                                        detail: {
                                            id: matchingSop.id,
                                            sop_number: matchingSop.sop_number,
                                            file_type: matchingSop.file_type,
                                            fromMasterRegister: true,
                                            masterRegisterId: masterRegisterId
                                        }
                                    }));
                                }, 100);
                            };
                        }
                    }
                });
                
            } catch (err) {
                // Cross-origin or other errors - silently fail
                console.log('Could not inject links (expected for cross-origin content)');
            }
        };

        // Wait for iframe to load
        iframe.addEventListener('load', injectLinks);
        
        // Also try immediately in case already loaded
        setTimeout(injectLinks, 500);

        return () => {
            iframe.removeEventListener('load', injectLinks);
        };
    }, [viewUrl, allSops, fileType, onClose, masterRegisterId]); // Added masterRegisterId

    const handleDownload = async () => {
        try {
            const response = await api.get(`/sops/${sopId}/download`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data]);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${sopNumber || 'SOP'}.${fileType || 'pdf'}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (err) {
            console.error('Error downloading SOP:', err);
            alert('Failed to download SOP. Please try again.');
        }
    };

    const handleClose = () => {
        if (viewUrl) {
            URL.revokeObjectURL(viewUrl);
        }
        onClose();
    };

    const handleBackToMasterRegister = () => {
        if (masterRegisterId) {
            // Close current viewer
            handleClose();
            
            // Open Master Register after a short delay
            setTimeout(() => {
                const masterRegister = allSops.find(sop => sop.id === masterRegisterId);
                if (masterRegister) {
                    window.dispatchEvent(new CustomEvent('openSOP', {
                        detail: {
                            id: masterRegister.id,
                            sop_number: masterRegister.sop_number,
                            file_type: masterRegister.file_type,
                            fromMasterRegister: false
                        }
                    }));
                }
            }, 100);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 animate-fadeIn" onClick={handleClose}>
            <div className="bg-white rounded-3xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col shadow-2xl animate-slideUp" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="bg-gradient-to-r from-[#475569] to-[#334155] p-6 rounded-t-3xl flex justify-between items-center text-white flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                            <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold">{sopNumber || 'SOP Document'}</h3>
                            <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-semibold uppercase tracking-wide mt-1">
                                {fileType?.toUpperCase() || 'UNKNOWN'}
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {fromMasterRegister && masterRegisterId && (
                            <button 
                                onClick={handleBackToMasterRegister} 
                                className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-all flex items-center gap-2 font-semibold"
                                title="Back to Master SOP Register"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span className="hidden md:inline">Back to Register</span>
                            </button>
                        )}
                        <button 
                            onClick={handleDownload} 
                            className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-all flex items-center gap-2 font-semibold"
                            title="Download SOP"
                        >
                            <Download className="w-4 h-4" />
                            <span className="hidden md:inline">Download</span>
                        </button>
                        <button 
                            onClick={handleClose} 
                            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-all flex items-center justify-center"
                            title="Close Viewer"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-hidden relative bg-gray-50">
                    {loading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
                            <Loader2 className="w-12 h-12 text-[#475569] animate-spin mb-4" />
                            <p className="text-lg text-gray-600 font-semibold">Loading SOP...</p>
                        </div>
                    )}

                    {error && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white p-8">
                            <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-500 rounded-2xl p-8 max-w-md text-center">
                                <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
                                <p className="text-lg text-red-900 mb-6">{error}</p>
                                <button 
                                    onClick={loadSopView} 
                                    className="px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white rounded-xl font-bold hover:-translate-y-0.5 transition-all shadow-lg flex items-center gap-2 mx-auto"
                                >
                                    <RotateCcw className="w-5 h-5" />
                                    <span>Retry</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {!loading && !error && viewUrl && (
                        <iframe
                            ref={iframeRef}
                            src={viewUrl}
                            className="w-full h-full border-0 bg-white"
                            title={sopNumber || 'SOP Document'}
                            sandbox="allow-same-origin allow-scripts"
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-t-2 border-[#475569] p-4 rounded-b-3xl flex-shrink-0">
                    <div className="flex items-center gap-3 text-sm text-[#475569] justify-center">
                        <Info className="w-5 h-5 flex-shrink-0" />
                        <p className="font-medium">
                            {sopNumber === 'OPS-Index-001' 
                                ? 'Click any SOP number in the table to view that SOP.' 
                                : 'For regulatory compliance, download and save this SOP for your records.'}
                        </p>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default SOPViewer;
